/**
 * FEN (Forsyth-Edwards Notation) utilities
 *
 * FEN string format: "piece-placement active-color castling en-passant halfmove fullmove"
 * Example: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
 */

import type { File, PieceType, Rank, Square } from "../types/common";
import { FILES, RANKS } from "../types/common";
import type { ChessState } from "../types/game-state";
import type { Piece } from "../types/piece";

/** Piece type to FEN character mapping */
const PIECE_TO_FEN: Record<PieceType, string> = {
  KING: "k",
  QUEEN: "q",
  ROOK: "r",
  BISHOP: "b",
  KNIGHT: "n",
  PAWN: "p",
};

/**
 * Get FEN character for a piece (uppercase for white, lowercase for black)
 */
function pieceToFenChar(piece: Piece): string {
  const char = PIECE_TO_FEN[piece.type];
  return piece.color === "WHITE" ? char.toUpperCase() : char;
}

/**
 * Convert piece placement to FEN string (ranks 8 to 1)
 */
function getPiecePlacement(state: ChessState): string {
  const ranks: string[] = [];

  // FEN goes from rank 8 (top) to rank 1 (bottom)
  for (let rankIdx = 7; rankIdx >= 0; rankIdx--) {
    let rankStr = "";
    let emptyCount = 0;

    for (let fileIdx = 0; fileIdx < 8; fileIdx++) {
      const square = state.board.squares[fileIdx]![rankIdx]!;

      if (square.pieceId) {
        // Flush empty count
        if (emptyCount > 0) {
          rankStr += emptyCount.toString();
          emptyCount = 0;
        }
        // Find the piece
        const piece = state.pieces.find((p) => p.id === square.pieceId);
        if (piece) {
          rankStr += pieceToFenChar(piece);
        }
      } else {
        emptyCount++;
      }
    }

    // Flush remaining empty count
    if (emptyCount > 0) {
      rankStr += emptyCount.toString();
    }

    ranks.push(rankStr);
  }

  return ranks.join("/");
}

/**
 * Get active color for FEN (w or b)
 */
function getActiveColor(state: ChessState): string {
  return state.currentPlayer === "WHITE" ? "w" : "b";
}

/**
 * Get castling availability string for FEN
 */
function getCastlingAvailability(state: ChessState): string {
  const { castlingRights } = state;
  let result = "";

  if (castlingRights.whiteKingside) result += "K";
  if (castlingRights.whiteQueenside) result += "Q";
  if (castlingRights.blackKingside) result += "k";
  if (castlingRights.blackQueenside) result += "q";

  return result || "-";
}

/**
 * Get en passant target square for FEN
 */
function getEnPassantTarget(state: ChessState): string {
  if (!state.enPassantTarget) return "-";
  return `${state.enPassantTarget.file}${state.enPassantTarget.rank}`;
}

/**
 * Convert game state to FEN notation
 *
 * @param state - The game state to convert
 * @returns FEN string representation
 *
 * @example
 * const fen = stateToFen(gameState);
 * // "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
 */
export function stateToFen(state: ChessState): string {
  const parts = [
    getPiecePlacement(state),
    getActiveColor(state),
    getCastlingAvailability(state),
    getEnPassantTarget(state),
    state.halfMoveClock.toString(),
    state.turnNumber.toString(),
  ];

  return parts.join(" ");
}

/**
 * Get a position hash for threefold repetition detection
 * Includes: piece placement, active color, castling rights, en passant target
 * Does NOT include: halfmove clock, fullmove number (not relevant for repetition)
 *
 * @param state - The game state to hash
 * @returns A string that uniquely identifies the position
 */
export function getPositionHash(state: ChessState): string {
  const parts = [
    getPiecePlacement(state),
    getActiveColor(state),
    getCastlingAvailability(state),
    getEnPassantTarget(state),
  ];

  return parts.join(" ");
}

/**
 * Parse a square notation (e.g., "e4") into a Square object
 */
function parseSquare(notation: string): Square | null {
  if (notation.length !== 2) return null;

  const file = notation[0]! as File;
  const rank = Number.parseInt(notation[1]!, 10) as Rank;

  if (!FILES.includes(file) || !RANKS.includes(rank)) {
    return null;
  }

  return { file, rank };
}

/**
 * Validation result for FEN parsing
 */
interface FenValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validate a FEN string format
 *
 * @param fen - The FEN string to validate
 * @returns Validation result with error message if invalid
 */
export function validateFen(fen: string): FenValidationResult {
  const parts = fen.trim().split(/\s+/);

  if (parts.length !== 6) {
    return {
      valid: false,
      error: `FEN must have 6 parts, got ${parts.length}`,
    };
  }

  const [placement, activeColor, castling, enPassant, halfMove, fullMove] =
    parts as [string, string, string, string, string, string];

  // Validate piece placement
  const ranks = placement.split("/");
  if (ranks.length !== 8) {
    return {
      valid: false,
      error: `Piece placement must have 8 ranks, got ${ranks.length}`,
    };
  }

  for (let i = 0; i < 8; i++) {
    let count = 0;
    for (const char of ranks[i]!) {
      if (/[1-8]/.test(char)) {
        count += Number.parseInt(char, 10);
      } else if (/[kqrbnpKQRBNP]/.test(char)) {
        count += 1;
      } else {
        return {
          valid: false,
          error: `Invalid character '${char}' in rank ${8 - i}`,
        };
      }
    }
    if (count !== 8) {
      return {
        valid: false,
        error: `Rank ${8 - i} has ${count} squares, expected 8`,
      };
    }
  }

  // Validate active color
  if (activeColor !== "w" && activeColor !== "b") {
    return {
      valid: false,
      error: `Invalid active color '${activeColor}', expected 'w' or 'b'`,
    };
  }

  // Validate castling
  if (!/^(-|[KQkq]+)$/.test(castling)) {
    return {
      valid: false,
      error: `Invalid castling rights '${castling}'`,
    };
  }

  // Validate en passant
  if (enPassant !== "-") {
    const square = parseSquare(enPassant);
    if (!square || (square.rank !== 3 && square.rank !== 6)) {
      return {
        valid: false,
        error: `Invalid en passant target '${enPassant}'`,
      };
    }
  }

  // Validate halfmove clock
  const halfMoveNum = Number.parseInt(halfMove, 10);
  if (Number.isNaN(halfMoveNum) || halfMoveNum < 0) {
    return {
      valid: false,
      error: `Invalid halfmove clock '${halfMove}'`,
    };
  }

  // Validate fullmove number
  const fullMoveNum = Number.parseInt(fullMove, 10);
  if (Number.isNaN(fullMoveNum) || fullMoveNum < 1) {
    return {
      valid: false,
      error: `Invalid fullmove number '${fullMove}'`,
    };
  }

  return { valid: true };
}
